import { AccountEltDestinationFragment } from "@/apollo/types";

type StreamSettings = {
  softDeleteEnabled?: boolean | null;
  enabledHistoryTable?: boolean | null;
  changeDataCaptureEnabled?: boolean | null;
};

type DestinationCapabilities = Pick<
  AccountEltDestinationFragment,
  "softDeletesSupported" | "historyTablesSupported" | "cdcDeletesSupported"
>;

const tables = (count: number) =>
  `${count} ${count === 1 ? "table" : "tables"}`;

/**
 * Postgres reports change data capture deletes in a file of their own that each destination applies;
 * other CDC sources carry them as rows every destination loads anyway. Mirrors
 * `postgresLikeIntegrationIds` in the backend's `integrations-list/postgres-like.ts`.
 */
const POSTGRES_LIKE_SOURCES = new Set([
  "postgresql",
  "postgresql-on-google-cloud",
  "postgresql-on-rds",
  "postgresql-on-azure-database",
  "heroku-postgres",
  "amazon-aurora-postgresql",
  "discourse",
  "plausible-analytics",
  "phoenix-via-postgresql",
  "supabase",
  "medusa",
]);

/** What the sync's tables use that the destination will not apply, one sentence each. */
export function skippedFeatures(
  destination: DestinationCapabilities,
  streams: StreamSettings[],
  sourceIntegrationId: string,
): string[] {
  const count = (enabled: (stream: StreamSettings) => unknown) =>
    streams.filter(enabled).length;

  const softDeletes = destination.softDeletesSupported
    ? 0
    : count((s) => s.softDeleteEnabled);
  const historyTables = destination.historyTablesSupported
    ? 0
    : count((s) => s.enabledHistoryTable);
  const cdcDeletes =
    destination.cdcDeletesSupported ||
    !POSTGRES_LIKE_SOURCES.has(sourceIntegrationId)
      ? 0
      : count((s) => s.changeDataCaptureEnabled);

  return [
    softDeletes > 0 &&
      `${tables(softDeletes)} with soft deletes won't mark deleted rows here.`,
    historyTables > 0 &&
      `${tables(historyTables)} with a history table won't get one here.`,
    cdcDeletes > 0 &&
      `${tables(cdcDeletes)} with change data capture won't apply source deletes here.`,
  ].filter((warning): warning is string => !!warning);
}
